import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Modal } from "bootstrap"
import { getCsrfToken } from "../utils/csrf.util"
import { announcePopoverOpen, bindExclusivePopover } from "../utils/exclusive-popover.util"
import { isAbortError, rethrowUnlessAbort } from "../utils/fetch-error.util"
import { disposeModal } from "../utils/modal-dispose.util"
import { isCompactViewport } from "../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion, prefersReducedMotion } from "../utils/popover-motion.util"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"
import { consumeToast, queueToast, showToast } from "../utils/toast.util"

const CLOSE_ANIMATION_DURATION = 360
const OPEN_ANIMATION_DURATION = 380
const PENDING_TOAST_STORAGE_KEY = "table-saved-views-pending-toast"

export default class extends Controller {
  static targets = [
    "button",
    "deleteConfirm",
    "deleteModal",
    "dirtyIndicator",
    "menu",
    "nameError",
    "nameForm",
    "nameInput",
    "nameModal",
    "nameSubmit",
    "nameTitle"
  ]
  static outlets = ["table-interactions"]
  static values = {
    tableKey: String,
    activeViewId: String,
    createUrl: String,
    defaultUrl: String,
    frameId: String,
    activeViewUrl: String
  }

  connect() {
    this.boundDocumentClick = this.#handleDocumentClick.bind(this)
    this.boundKeydown = this.#handleKeydown.bind(this)
    this.boundNameDialogHidden = this.#handleNameDialogHidden.bind(this)
    this.boundNameDialogShown = this.#handleNameDialogShown.bind(this)
    this.boundNameDialogHide = this.#handleNameDialogHide.bind(this)
    this.boundNameDialogSubmit = this.#submitNameDialog.bind(this)
    this.boundNameDialogInput = () => this.#setNameDialogError(false)
    this.boundDeleteConfirm = this.confirmDeleteView.bind(this)
    this.boundDeleteView = this.deleteView.bind(this)
    this.boundWindowResize = this.#positionMenu.bind(this)
    this.boundQueryChanged = this.#handleQueryChanged.bind(this)
    this.boundColumnsChanged = this.#handleColumnsChanged.bind(this)
    this.closeAnimation = null
    this.backdropAnimation = null
    this.isAnimatingNameDialogClose = false
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.#setOpen(false))
    document.addEventListener("click", this.boundDocumentClick, true)
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("resize", this.boundWindowResize, { passive: true })
    document.addEventListener("lucanto:table-query-changed", this.boundQueryChanged)
    document.addEventListener("lucanto:table-columns-changed", this.boundColumnsChanged)
    if (this.frameIdValue) {
      this.tableNavigator = new TableFrameNavigator({ frameId: this.frameIdValue, tableKey: this.tableKeyValue })
    }
    if (this.hasNameModalTarget) {
      this.#setupNameDialog()
    }
    if (this.hasDeleteModalTarget) {
      this.#setupDeleteDialog()
    }
    this.deleteButtons = Array.from(this.element.querySelectorAll("[data-table-saved-views-delete-button]"))
    for (const button of this.deleteButtons) button.addEventListener("click", this.boundDeleteView)
    consumeToast(PENDING_TOAST_STORAGE_KEY)
    this.#syncDirtyFromUrl()
  }

  disconnect() {
    document.removeEventListener("click", this.boundDocumentClick, true)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("resize", this.boundWindowResize)
    document.removeEventListener("lucanto:table-query-changed", this.boundQueryChanged)
    document.removeEventListener("lucanto:table-columns-changed", this.boundColumnsChanged)
    this.tableNavigator?.disconnect()
    if (this.nameDialog) {
      this.nameDialog.modal.removeEventListener("hidden.bs.modal", this.boundNameDialogHidden)
      this.nameDialog.modal.removeEventListener("shown.bs.modal", this.boundNameDialogShown)
      this.nameDialog.modal.removeEventListener("hide.bs.modal", this.boundNameDialogHide)
      this.nameDialog.form?.removeEventListener("submit", this.boundNameDialogSubmit)
      this.nameDialog.input?.removeEventListener("input", this.boundNameDialogInput)
    }
    for (const button of this.deleteButtons || []) button.removeEventListener("click", this.boundDeleteView)
    this.deleteConfirmationConfirm?.removeEventListener("click", this.boundDeleteConfirm)
    disposeModal(this.nameModal)
    disposeModal(this.deleteConfirmationModal)
    this.closeAnimation?.cancel()
    this.backdropAnimation?.cancel()
    this.#resolveNameDialog(null)
    this.nameDialog?.modal.remove()
    this.deleteConfirmationElement?.remove()
    this.nameDialog = null
    this.pendingDeleteButton = null
    this.unbindExclusivePopover?.()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    this.#setOpen(this.menuTarget.hidden)
  }

  async saveNewView(event) {
    event.preventDefault()
    event.stopPropagation()

    const name = await this.#openNameDialog({
      title: this.element.dataset.newViewPrompt,
      submitLabel: this.element.dataset.newViewSubmit,
      initialValue: ""
    })
    if (!name) return

    this.#setNameDialogSubmitting(true)
    this.#request(this.createUrlValue, "POST", {
      saved_table_view: {
        table_key: this.tableKeyValue,
        name,
        state: this.#captureState()
      }
    })
      .then((payload) => {
        if (payload.toast) queueToast(PENDING_TOAST_STORAGE_KEY, payload.toast)
        this.nameModal?.hide()
        this.#visitView(payload.view)
      })
      .catch((error) => this.#handleDialogError(error))
  }

  saveToView(event) {
    event.preventDefault()
    event.stopPropagation()

    this.#request(event.currentTarget.dataset.url, "PATCH", {
      saved_table_view: { state: this.#captureState() }
    })
      .then((payload) => {
        if (payload.toast) queueToast(PENDING_TOAST_STORAGE_KEY, payload.toast)
        this.#visitView(payload.view)
      })
      .catch((error) => this.#handleError(error))
  }

  duplicateView(event) {
    event.preventDefault()
    event.stopPropagation()

    this.#request(event.currentTarget.dataset.url, "POST", {})
      .then((payload) => {
        if (payload.toast) queueToast(PENDING_TOAST_STORAGE_KEY, payload.toast)
        this.#visitView(payload.view)
      })
      .catch((error) => this.#handleError(error))
  }

  makeDefaultView(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget

    this.#request(button.dataset.url, "PATCH", {})
      .then(() => {
        this.#markDefaultView(button)
        showToast({ type: "success", message: button.dataset.toastMessage })
      })
      .catch((error) => this.#handleError(error))
  }

  async renameView(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const name = await this.#openNameDialog({
      title: this.element.dataset.renameViewPrompt,
      submitLabel: this.element.dataset.renameViewSubmit,
      initialValue: button.dataset.viewName || ""
    })
    if (!name) return

    this.#setNameDialogSubmitting(true)
    this.#request(button.dataset.url, "PATCH", {
      saved_table_view: { name }
    })
      .then((payload) => {
        if (payload.toast) queueToast(PENDING_TOAST_STORAGE_KEY, payload.toast)
        this.nameModal?.hide()
        this.#visitView(payload.view)
      })
      .catch((error) => this.#handleDialogError(error))
  }

  #setupNameDialog() {
    const modal = this.nameModalTarget
    this.nameDialog = {
      modal,
      form: modal.querySelector("[data-table-saved-views-target~='nameForm']"),
      input: modal.querySelector("[data-table-saved-views-target~='nameInput']"),
      error: modal.querySelector("[data-table-saved-views-target~='nameError']"),
      submit: modal.querySelector("[data-table-saved-views-target~='nameSubmit']"),
      title: modal.querySelector("[data-table-saved-views-target~='nameTitle']")
    }

    document.body.appendChild(modal)
    this.nameModal = Modal.getOrCreateInstance(modal)
    modal.addEventListener("hidden.bs.modal", this.boundNameDialogHidden)
    modal.addEventListener("shown.bs.modal", this.boundNameDialogShown)
    modal.addEventListener("hide.bs.modal", this.boundNameDialogHide)
    this.nameDialog.form?.addEventListener("submit", this.boundNameDialogSubmit)
    this.nameDialog.input?.addEventListener("input", this.boundNameDialogInput)
  }

  #setupDeleteDialog() {
    this.deleteConfirmationElement = this.deleteModalTarget
    this.deleteConfirmationConfirm = this.deleteConfirmationElement.querySelector(
      "[data-table-saved-views-target~='deleteConfirm']"
    )
    document.body.appendChild(this.deleteConfirmationElement)
    this.deleteConfirmationModal = Modal.getOrCreateInstance(this.deleteConfirmationElement)
    this.deleteConfirmationConfirm?.addEventListener("click", this.boundDeleteConfirm)
  }

  #submitNameDialog(event) {
    event.preventDefault()

    const name = this.nameDialog.input.value.trim()
    if (!name) {
      this.#setNameDialogError(this.nameDialog.error.dataset.requiredMessage)
      this.nameDialog.input.focus()
      return
    }

    if (this.#nameExists(name)) {
      this.#setNameDialogError(this.nameDialog.error.dataset.duplicateMessage)
      this.nameDialog.input.focus()
      return
    }

    this.#resolveNameDialog(name)
  }

  deleteView(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.deleteConfirmationModal) return

    this.pendingDeleteButton = event.currentTarget
    this.deleteConfirmationModal.show()
  }

  confirmDeleteView(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = this.pendingDeleteButton
    if (!button) return

    const deletingActiveView = button.dataset.viewId === this.activeViewIdValue
    this.deleteConfirmationConfirm.disabled = true

    this.#request(button.dataset.url, "DELETE", { activate_first_view: deletingActiveView })
      .then((payload) => {
        if (payload.toast) queueToast(PENDING_TOAST_STORAGE_KEY, payload.toast)
        this.deleteConfirmationModal.hide()

        if (deletingActiveView) {
          this.#setDirty(false)
          Turbo.visit(this.defaultUrlValue)
          return
        }

        button.closest("[data-saved-view-name]")?.remove()
        if (payload.toast) {
          window.sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY)
          showToast(payload.toast)
        }
      })
      .catch((error) => this.#handleError(error))
      .finally(() => {
        this.pendingDeleteButton = null
        this.deleteConfirmationConfirm.disabled = false
      })
  }

  #captureState() {
    return {
      columns: this.hasTableInteractionsOutlet ? this.tableInteractionsOutlet.exportColumnState() : {},
      filters: { params: this.#currentFilterParams() },
      sorting: this.#currentSortingParams()
    }
  }

  #currentFilterParams() {
    const params = new URLSearchParams(window.location.search)
    params.delete("page")
    params.delete("view")
    params.delete("saved_view_id")
    params.delete("sort")
    params.delete("direction")

    const result = {}
    for (const [key, value] of params.entries()) {
      if (result[key]) {
        result[key] = Array.isArray(result[key]) ? [...result[key], value] : [result[key], value]
      } else {
        result[key] = value
      }
    }

    return result
  }

  #currentSortingParams() {
    const params = new URLSearchParams(window.location.search)
    const column = params.get("sort")
    const direction = params.get("direction")

    if (!column || !["asc", "desc"].includes(direction)) return {}

    return { column, direction }
  }

  #visitView(view) {
    const params = new URLSearchParams()
    const filterParams = view?.state?.filters?.params || {}
    const sorting = view?.state?.sorting || {}

    for (const [key, value] of Object.entries(filterParams)) {
      for (const item of Array.isArray(value) ? value : [value]) {
        if (item !== null && item !== undefined && item !== "") params.append(key, item)
      }
    }
    if (sorting.column && sorting.direction) {
      params.set("sort", sorting.column)
      params.set("direction", sorting.direction)
    }
    params.set("saved_view_id", view.id)

    const url = `${this.defaultUrlValue}?${params.toString()}`
    this.#setDirty(false)
    if (this.tableNavigator) {
      const parsedUrl = new URL(url, window.location.href)
      this.tableNavigator.navigate(new TableQueryState(parsedUrl.search), {
        history: "push",
        reason: "saved-view",
        baseUrl: `${parsedUrl.origin}${parsedUrl.pathname}`,
        preserveScroll: { x: false, y: false }
      })
    } else {
      Turbo.visit(url)
    }
  }

  #handleQueryChanged(event) {
    if (!this.activeViewIdValue || event.detail?.frameId !== this.frameIdValue) return
    if (event.detail?.result && event.detail.result !== "started") return
    if (event.detail?.reason === "saved-view") {
      this.#setDirty(false)
      return
    }
    if (["filter", "search", "sort"].includes(event.detail?.reason)) this.#setDirty(true)
  }

  #handleColumnsChanged(event) {
    if (!this.activeViewIdValue) return
    const tableRoot = this.element.closest(".documents-table-controller")
    if (event.detail?.tableElement === tableRoot) this.#setDirty(true)
  }

  #syncDirtyFromUrl() {
    if (!(this.activeViewIdValue && this.activeViewUrlValue)) {
      this.#setDirty(false)
      return
    }
    this.#setDirty(this.#queryFingerprint(window.location.href) !== this.#queryFingerprint(this.activeViewUrlValue))
  }

  #queryFingerprint(source) {
    return new TableQueryState(new URL(source, window.location.href).search).fingerprint({
      exclude: ["page", "saved_view_id", "view"]
    })
  }

  #setDirty(dirty) {
    this.element.dataset.dirty = dirty.toString()
    this.element.classList.toggle("is-dirty", dirty)
    if (this.hasDirtyIndicatorTarget) this.dirtyIndicatorTarget.hidden = !dirty
    if (!this.hasButtonTarget) return
    const baseLabel = this.buttonTarget.dataset.baseAriaLabel || this.buttonTarget.textContent.trim()
    this.buttonTarget.dataset.baseAriaLabel = baseLabel
    const dirtyLabel = this.element.dataset.dirtyLabel || "Unsaved changes"
    this.buttonTarget.setAttribute("aria-label", dirty ? `${baseLabel}. ${dirtyLabel}` : baseLabel)
  }

  #request(url, method, body) {
    return fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: method === "GET" ? undefined : JSON.stringify(body)
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        const error = new Error(payload.errors?.join(", ") || response.statusText)
        // Carried so the terminal handlers can tell a server ANSWER from a
        // server FAILURE — see #isUserFacing.
        error.status = response.status
        throw error
      }

      return payload
    })
  }

  #handleDocumentClick(event) {
    if (this.menuTarget.hidden || this.element.contains(event.target)) return

    this.#setOpen(false)
  }

  #handleKeydown(event) {
    if (event.key === "Escape") this.#setOpen(false)
  }

  #openNameDialog({ title, submitLabel, initialValue }) {
    if (!this.nameDialog) return Promise.resolve(null)

    this.#setOpen(false)
    this.#resolveNameDialog(null)
    this.nameDialog.title.textContent = title || ""
    this.nameDialog.submit.textContent = submitLabel || ""
    this.nameDialog.input.value = initialValue || ""
    this.nameDialog.initialName = initialValue || ""
    this.nameDialog.input.setAttribute("aria-label", title || "")
    this.#setNameDialogError(false)
    this.#setNameDialogSubmitting(false)

    return new Promise((resolve) => {
      this.nameDialogResolve = resolve
      this.nameModal?.show()
    })
  }

  #handleNameDialogShown() {
    this.#decorateNameDialogBackdrop()
    this.nameDialog.modal.classList.add("is-opening")
    this.nameDialog.input.focus()
    this.nameDialog.input.select()
    window.setTimeout(() => this.nameDialog?.modal.classList.remove("is-opening"), OPEN_ANIMATION_DURATION)
  }

  #handleNameDialogHide(event) {
    if (this.isAnimatingNameDialogClose || prefersReducedMotion()) return

    event.preventDefault()
    this.isAnimatingNameDialogClose = true
    this.nameDialog.modal.classList.remove("is-opening")
    this.nameDialog.modal.classList.add("is-closing")
    this.#playNameDialogCloseAnimation()
  }

  #handleNameDialogHidden() {
    this.nameDialog.modal.classList.remove("is-opening", "is-closing")
    this.isAnimatingNameDialogClose = false
    this.#setNameDialogSubmitting(false)
    this.#resolveNameDialog(null)
  }

  #decorateNameDialogBackdrop() {
    const backdrop = document.querySelector(".modal-backdrop:last-of-type")
    if (!backdrop) return

    backdrop.classList.add("lc-invitation-modal-backdrop", "is-visible")
    this.nameDialog.backdrop = backdrop
  }

  #resolveNameDialog(value) {
    const resolve = this.nameDialogResolve
    this.nameDialogResolve = null
    resolve?.(value)
  }

  #setNameDialogError(visible) {
    if (!this.nameDialog?.error) return

    this.nameDialog.error.hidden = !visible
    this.nameDialog.error.textContent =
      typeof visible === "string" ? visible : this.nameDialog.error.dataset.requiredMessage
    this.nameDialog.input.setAttribute("aria-invalid", (!!visible).toString())
  }

  #setNameDialogSubmitting(submitting) {
    if (!this.nameDialog?.submit) return

    this.nameDialog.submit.disabled = submitting
    this.nameDialog.input.disabled = submitting
  }

  #nameExists(name) {
    const initialName = this.nameDialog?.initialName || ""
    const normalizedName = this.#normalizeViewName(name)
    if (normalizedName === this.#normalizeViewName(initialName)) return false

    return Array.from(this.element.querySelectorAll("[data-saved-view-name]")).some((element) => {
      return this.#normalizeViewName(element.dataset.savedViewName) === normalizedName
    })
  }

  #normalizeViewName(name) {
    return (name || "").trim().toLocaleLowerCase()
  }

  #playNameDialogCloseAnimation() {
    this.closeAnimation?.cancel()
    this.backdropAnimation?.cancel()

    const surface = this.nameDialog.modal.querySelector(".modal-content")
    this.closeAnimation = surface?.animate?.(
      [
        { opacity: 1, transform: "translate3d(0, 0, 0) scale(1, 1)" },
        { opacity: 0, transform: "translate3d(0, -16px, 0) scale(0.996, 0.996)" }
      ],
      {
        duration: CLOSE_ANIMATION_DURATION,
        easing: "cubic-bezier(0.32, 0.72, 0, 1)",
        fill: "both"
      }
    )

    if (this.nameDialog.backdrop) {
      this.backdropAnimation = this.nameDialog.backdrop.animate?.(
        [
          { opacity: Number.parseFloat(window.getComputedStyle(this.nameDialog.backdrop).opacity) || 1 },
          { opacity: 0 }
        ],
        {
          duration: CLOSE_ANIMATION_DURATION,
          easing: "cubic-bezier(0.32, 0.72, 0, 1)",
          fill: "both"
        }
      )
    }

    Promise.allSettled([this.closeAnimation?.finished, this.backdropAnimation?.finished].filter(Boolean)).finally(
      () => {
        this.nameModal?.hide()
      }
    )
  }

  // A 4xx is the server ANSWERING — a duplicate view name, a validation
  // failure. That message belongs to the user and must NOT be reported: doing
  // so files a monitoring alert every time someone reuses a name. A 5xx, or a
  // fetch that never got a response at all, is a genuine failure and has to
  // reach the global handler.
  #isUserFacing(error) {
    return error?.status >= 400 && error.status < 500
  }

  // Same contract as #handleError, but for the two flows whose failure belongs
  // inline in the name dialog rather than in a toast. The abort guard matters
  // here: re-enabling the form and painting an error after the user has
  // navigated away would undo what they just did (§2).
  #handleDialogError(error) {
    if (!isAbortError(error)) {
      this.#setNameDialogSubmitting(false)
      this.#setNameDialogError(error.message)
    }
    if (this.#isUserFacing(error)) return
    rethrowUnlessAbort(error)
  }

  // Terminal handler for every saved-view request. Surfaces the failure in the
  // app's own toast — not a native alert — then re-raises so the global handler
  // reports it (frontend/stimulus-controllers.md §1). An abort is the user
  // moving on: no toast, no report.
  #handleError(error) {
    if (!isAbortError(error)) {
      showToast({ type: "error", message: error.message })
    }
    if (this.#isUserFacing(error)) return
    rethrowUnlessAbort(error)
  }

  #markDefaultView(activeButton) {
    const defaultButtons = this.element.querySelectorAll("[data-table-saved-views-default-button]")

    defaultButtons.forEach((button) => {
      const isDefault = button === activeButton
      button.classList.toggle("is-default", isDefault)
      button.setAttribute("aria-pressed", isDefault.toString())
    })
  }

  #setOpen(open) {
    if (open) {
      announcePopoverOpen(this.element)
      cancelPopoverClose(this.menuTarget)
      this.menuTarget.hidden = false
      this.#positionMenu()
    } else if (!this.menuTarget.hidden) {
      closeWithPopoverMotion(this.menuTarget, () => {
        this.menuTarget.hidden = true
        this.#clearMenuPosition()
      })
    } else {
      this.#clearMenuPosition()
    }
    this.buttonTarget.setAttribute("aria-expanded", open.toString())
    this.buttonTarget.classList.toggle("is-active", open)
  }

  #positionMenu() {
    if (!this.hasMenuTarget || this.menuTarget.hidden) return

    if (!isCompactViewport()) {
      this.#clearMenuPosition()
      return
    }

    const spacing = 8
    const buttonRect = this.buttonTarget.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const top = Math.max(spacing, Math.floor(buttonRect.bottom + spacing))
    this.menuTarget.style.position = "fixed"
    this.menuTarget.style.top = `${top}px`
    this.menuTarget.style.left = `${spacing}px`
    this.menuTarget.style.right = `${spacing}px`
    this.menuTarget.style.width = "auto"
    this.menuTarget.style.maxHeight = `${Math.max(240, viewportHeight - top - spacing)}px`
    this.menuTarget.style.zIndex = "1004"
  }

  #clearMenuPosition() {
    if (!this.hasMenuTarget) return

    this.menuTarget.style.removeProperty("position")
    this.menuTarget.style.removeProperty("top")
    this.menuTarget.style.removeProperty("left")
    this.menuTarget.style.removeProperty("right")
    this.menuTarget.style.removeProperty("width")
    this.menuTarget.style.removeProperty("max-height")
    this.menuTarget.style.removeProperty("z-index")
  }
}
